import { toPng } from 'html-to-image';
import { autoCropWhiteMargins, cropToBoundingBox, BoundingBox, preScaleImage } from './imageCropping'; // Added preScaleImage

/**
 * Captures the React Flow diagram area as an image.
 * Assumes the caller has already adjusted the viewport to fit the selected group node.
 * This function is designed to work with a single selected group node.
 * The caller should verify that a group node is selected before calling this function.
 * 
 * @param clipArea Optional bounding box to crop the captured image to. Coordinates are relative to the captured .react-flow__renderer element.
 * @param options Optional configuration options for the capture process
 * @param options.addWhiteBackground Whether to add a white background to the image (useful for PDF generation)
 * @param options.cropEmptySpace Whether to automatically crop out empty white space (useful for PDF generation)
 * @param options.preScaleForPdf Whether to pre-scale the image to an optimal resolution for PDF embedding
 * @returns Promise that resolves to the captured (and potentially cropped/scaled) image as a base64 string
 */
export const captureDiagram = async (
  clipArea?: BoundingBox,
  options?: {
    addWhiteBackground?: boolean;
    cropEmptySpace?: boolean;
    preScaleForPdf?: boolean; // New option
  }
): Promise<string> => {
  try {
    
    // The caller (e.g., useClipboardImage) is responsible for:
    // 1. Verifying a single group node is selected
    // 2. Calling fitView on the group node (not its children)
    // 3. Providing the appropriate clipArea for the group
    // We wait a moment here to ensure any pending view updates from the caller are applied.
    await new Promise(resolve => setTimeout(resolve, 50)); // Reduced delay, caller has its own delay
    
    // Find the React Flow container (just the canvas, not the sidebars)
    const reactFlowCanvas = document.querySelector('.react-flow__renderer');
    
    if (!reactFlowCanvas) {
      throw new Error('React Flow canvas not found');
    }
    
    // Wait a moment for the UI to update to light mode
    await new Promise(resolve => setTimeout(resolve, 100));

    // Inject temporary styles to hide selection but preserve all node content
    const styleId = 'temp-hide-selection-style';
    const tempStyle = document.createElement('style');
    tempStyle.id = styleId;
    tempStyle.innerHTML = `
      /* --- Node Selection Hiding --- */
      .react-flow__node.selected,
      .react-flow__node.selected:hover,
      .react-flow__node:focus,
    `;
    document.head.appendChild(tempStyle);

    let processedDataUrl = '';
    
    try {
      // Capture the diagram as PNG
      const dataUrl = await toPng(reactFlowCanvas as HTMLElement, {
        quality: 1,
        pixelRatio: 3,
        skipAutoScale: true, 
        skipFonts: true, // Skip processing fonts to avoid CORS issues with Google Fonts
        // Add white background if requested
        style: options?.addWhiteBackground ? { background: 'white' } : undefined,
        // imagePlaceholder is still useful for broken images.
        imagePlaceholder: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=='
      });
      processedDataUrl = dataUrl;
    } finally {
      // Always remove the temporary style element
      const styleElementToRemove = document.getElementById(styleId);
      if (styleElementToRemove) {
        styleElementToRemove.remove();
      }

      // Restore dark mode if it was active before
      // if (wasDarkMode) {
      //   htmlElement.classList.add('dark');
      // }
      // ^^^ MODIFICATION: Removed automatic theme switching.
    }
    
    // If a clipArea is provided, crop to that bounding box first
    if (clipArea) {
      const pixelRatioForScaling = 3; // Matches the pixelRatio used in toPng options
      const scaledClipArea: BoundingBox = {
        x: clipArea.x * pixelRatioForScaling,
        y: clipArea.y * pixelRatioForScaling,
        width: clipArea.width * pixelRatioForScaling,
        height: clipArea.height * pixelRatioForScaling,
      };

      // Cropping logic
      processedDataUrl = await cropToBoundingBox(processedDataUrl, scaledClipArea);
    }
    
    // Apply auto-cropping of white margins if requested
    if (options?.cropEmptySpace) {
      processedDataUrl = await autoCropWhiteMargins(processedDataUrl);
    }

    // Pre-scale for PDF if requested
    if (options?.preScaleForPdf) {
      const PDF_TARGET_POINT_WIDTH = 467; // Points in PDF
      const PDF_TARGET_POINT_HEIGHT = 240; // Points in PDF
      const TARGET_DPI_FOR_PDF = 200; // Target DPI for the image in PDF

      // Calculate target pixel dimensions for the pre-scaled image
      const intermediateWidthPx = Math.round((PDF_TARGET_POINT_WIDTH / 72) * TARGET_DPI_FOR_PDF);
      const intermediateHeightPx = Math.round((PDF_TARGET_POINT_HEIGHT / 72) * TARGET_DPI_FOR_PDF);
      
      processedDataUrl = await preScaleImage(processedDataUrl, intermediateWidthPx, intermediateHeightPx);
    }
    
    return processedDataUrl;
  } catch (error) {
    console.error('Error capturing diagram:', error);
    throw error;
  }
};
